import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSendTransfer } from './useSendTransfer';
import { useSession } from './useSession';
import { TransferSettlementError, waitForTransferSettlement } from '../lib/transfers';
import {
  offrampRatesApi,
  offrampKycApi,
  offrampQuoteApi,
  offrampCreateApi,
  offrampGetApi,
  offrampListApi,
  isFinalPayout,
  type OfframpQuote,
  type Payout,
} from '../lib/offramp';

export type SellStage = 'idle' | 'quoting' | 'paying' | 'opening' | 'settling';

/** The channels open right now. Empty means the provider has none enabled. */
export function useOfframpChannels() {
  const { token } = useSession();
  return useQuery({
    queryKey: ['offrampChannels'],
    enabled: !!token,
    queryFn: () => offrampRatesApi(),
    staleTime: 12_000,
    refetchOnMount: 'always',
  });
}

/** Whether this user has cleared verification, and under what name. */
export function useOfframpKyc() {
  const { token } = useSession();
  return useQuery({
    queryKey: ['offrampKyc'],
    enabled: !!token,
    queryFn: () => offrampKycApi(),
    staleTime: 30_000,
  });
}

export function useOfframpPayouts() {
  const { token } = useSession();
  return useQuery({
    queryKey: ['offrampPayouts'],
    enabled: !!token,
    queryFn: () => offrampListApi(),
    staleTime: 30_000,
  });
}

/**
 * Follow one payout until it stops moving.
 *
 * The provider has no endpoint to read a payout, so the server re-sends the
 * idempotent create to learn its state; that is also the only signal we trust
 * while the callback format is undocumented.
 */
export function usePayoutStatus(id: string | null) {
  return useQuery({
    queryKey: ['payout', id],
    enabled: !!id,
    queryFn: () => offrampGetApi(id!),
    refetchInterval: (q) => {
      const row = q.state.data;
      if (!row || isFinalPayout(row.status)) return false;
      const age = Date.now() - new Date(row.createdAt).getTime();
      if (age > 15 * 60_000) return false;
      return age > 60_000 ? 15_000 : 8_000;
    },
  });
}

/**
 * Cashing out: price it, pay our treasury, wait until that transfer is confirmed,
 * then ask Antarctic to pay the recipient from the merchant balance.
 *
 * Antarctic has no per-payout TRON address. Opening the order before the chain
 * confirms leaves the USDT collected and the payout never created. After
 * broadcast the transfer is retained, so a failed open retries without a second
 * charge.
 */
export function useSellCrypto() {
  const qc = useQueryClient();
  const { send } = useSendTransfer();
  const [stage, setStage] = useState<SellStage>('idle');
  const activeRef = useRef<Promise<Payout> | null>(null);
  const paidRef = useRef<{
    type: string;
    currency: string;
    fiatAmount: string;
    transferId: string;
    rate: number;
  } | null>(null);

  function sell(params: {
    quote: OfframpQuote;
    recipient: Record<string, string>;
    walletId: string;
    index: number;
    address: string;
    reauthToken?: string;
  }): Promise<Payout> {
    if (activeRef.current) return activeRef.current;
    const task = runSell(params).finally(() => {
      activeRef.current = null;
    });
    activeRef.current = task;
    return task;
  }

  async function runSell(params: {
    quote: OfframpQuote;
    recipient: Record<string, string>;
    walletId: string;
    index: number;
    address: string;
    reauthToken?: string;
  }): Promise<Payout> {
    const { quote, recipient, walletId, index, address, reauthToken } = params;
    try {
      let paid = paidRef.current;
      if (
        paid &&
        (paid.type !== quote.type ||
          paid.currency !== quote.currency ||
          paid.fiatAmount !== String(quote.fiatAmount))
      ) {
        paid = null;
        paidRef.current = null;
      }

      if (!paid) {
        // Re-price at the last moment: the form takes long enough for the rate
        // to move, and this is also the float check before any USDT leaves.
        setStage('quoting');
        const fresh = await offrampQuoteApi(quote.type, quote.currency, quote.totalUsdt.toFixed(6));
        // Any change the confirm screen would show is a new amount to accept.
        if (
          fresh.totalUsdt.toFixed(6) !== quote.totalUsdt.toFixed(6) ||
          fresh.fiatAmount.toFixed(2) !== quote.fiatAmount.toFixed(2)
        ) {
          const moved = new Error('rate_moved') as Error & { quote: OfframpQuote };
          moved.quote = fresh;
          throw moved;
        }

        setStage('paying');
        const { transferId } = await send({
          walletId,
          index,
          address,
          to: fresh.payTo,
          amount: fresh.totalUsdt.toFixed(6),
          coin: 'USDT',
          reauthToken,
        });
        if (!transferId) throw new Error('payment_failed');
        paid = {
          type: fresh.type,
          currency: fresh.currency,
          fiatAmount: String(fresh.fiatAmount),
          transferId,
          rate: fresh.rate,
        };
        paidRef.current = paid;
      }

      setStage('settling');
      try {
        await waitForTransferSettlement(paid.transferId);
      } catch (error) {
        if (error instanceof TransferSettlementError) paidRef.current = null;
        throw error;
      }

      setStage('opening');
      const payout = await offrampCreateApi({
        type: paid.type,
        currency: paid.currency,
        fiatAmount: paid.fiatAmount,
        recipient,
        quotedRate: paid.rate,
        paymentTransferId: paid.transferId,
        idempotencyKey: paid.transferId,
        reauthToken,
      });

      // A failed payout already has the transfer on the row. Keep it so a retry
      // reopens that same order instead of charging again.
      if (payout.status !== 'failed') paidRef.current = null;

      void qc.invalidateQueries({ queryKey: ['offrampPayouts'] });
      void qc.invalidateQueries({ queryKey: ['txs'] });
      setStage(isFinalPayout(payout.status) ? 'idle' : 'settling');
      return payout;
    } catch (e) {
      setStage('idle');
      const err = e instanceof Error ? e : new Error(String(e));
      if (paidRef.current) (err as Error & { paid?: boolean }).paid = true;
      throw err;
    }
  }

  return { stage, sell };
}
